import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Header from '../../components/navigation/Header';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    if (!email) return 'Email is required';
    if (!/\S+@\S+\.\S+/?.test(email)) return 'Enter a valid email address';
    return '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationError = validate();
    if (validationError) { setEmailError(validationError); return; }

    setIsSubmitting(true);
    setErrorMessage('');

    const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `${window.location?.origin}/reset-password`,
    });

    setIsSubmitting(false);

    if (error) {
      setErrorMessage(error?.message || 'Failed to send reset email. Please try again.');
    } else {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Header user={null} onNavigate={() => {}} onPurchaseCredits={() => {}} onLogout={() => {}} />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-500 shadow-lg mb-4">
              <Icon name="Camera" size={28} color="white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Angle</h1>
            <p className="text-gray-500 text-sm mt-1">Find your best angle with AI</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {!submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-4">
                    <Icon name="KeyRound" size={28} color="#7c3aed" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Forgot your password?</h2>
                  <p className="text-sm text-gray-500">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                </div>

                {errorMessage && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-5">
                    <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e?.target?.value); setEmailError(''); setErrorMessage(''); }}
                      placeholder="you@example.com"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        emailError ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                  >
                    {isSubmitting ? (
                      <><Icon name="Loader2" size={18} className="animate-spin" /> Sending link...</>
                    ) : (
                      <><Icon name="Send" size={18} /> Send Reset Link</>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => navigate('/auth')}
                      className="text-sm text-gray-500 hover:text-purple-600 transition-colors flex items-center gap-1 mx-auto"
                    >
                      <Icon name="ArrowLeft" size={14} />
                      Back to Sign In
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-5">
                  <Icon name="MailCheck" size={32} color="#16a34a" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Check your inbox</h2>
                <p className="text-sm text-gray-500 mb-6">
                  We've sent a password reset link to <span className="font-semibold text-gray-700">{email}</span>. The link expires in 1 hour.
                </p>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6 text-left">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={16} color="#7c3aed" className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-purple-700">
                      Didn't receive the email? Check your spam folder or try again with a different email address.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full py-3 px-4 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
                >
                  Back to Sign In
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
